import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import LandingPage from "@/pages/LandingPage";
import ReservationWizard from "@/pages/ReservationWizard";
import AuthPage from "@/pages/AuthPage";
import MyReservations from "@/pages/MyReservations";
import AdminPanel from "@/pages/AdminPanel";
import SedePanel from "@/pages/SedePanel";

function PaymentResult({ status }) {
  const navigate = useNavigate();
  const isSuccess = status === "success";
  return (
    <div className="min-h-screen bg-[#0f0f1a] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isSuccess ? 'bg-green-900/20 border-2 border-green-500' : 'bg-red-900/20 border-2 border-red-500'}`}>
          {isSuccess ? (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4caf50" strokeWidth="2.5"><path d="M5 13l4 4L19 7" /></svg>
          ) : (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef5350" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
          )}
        </div>
        <h2 className="text-2xl font-bold text-[#f0ece2] mb-2">{isSuccess ? "Pago exitoso" : "Pago fallido"}</h2>
        <p className="text-sm text-[#a0a0b8] mb-6">{isSuccess ? "Tu pago ha sido procesado correctamente." : "Hubo un problema al procesar tu pago. Int\u00e9ntalo nuevamente."}</p>
        <button onClick={() => navigate(isSuccess ? "/acceder" : "/reservar")} className="px-6 py-3 rounded-lg bg-[#c9a84c] text-[#0f0f1a] font-semibold hover:bg-[#d4b55b] transition-colors">
          {isSuccess ? "Ver mis reservas" : "Intentar de nuevo"}
        </button>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/acceder" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;
  return children;
}

function ImpersonationWrapper() {
  const { impersonating, exitImpersonation, user } = useAuth();
  const navigate = useNavigate();

  const handleExit = async () => {
    await exitImpersonation();
    navigate("/admin");
  };

  if (!impersonating) return null;
  return <ImpersonationBanner user={user} onExit={handleExit} />;
}

function App() {
  // Console branding (once only) + suppress infrastructure noise
  useEffect(() => {
    if (window.__DATALAB_BRANDED__) return;
    window.__DATALAB_BRANDED__ = true;
    // Suppress network errors from infrastructure scripts (Cloudflare, etc.)
    window.addEventListener("error", (e) => {
      if (e.message?.includes("ERR_BLOCKED_BY_CLIENT") || e.target?.src?.includes("cloudflareinsights") || e.target?.src?.includes("beacon.min.js")) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
    const ascii = `██╗    ██╗███████╗     █████╗ ██████╗ ███████╗    ██████╗  █████╗ ████████╗ █████╗ ██╗      █████╗ ██████╗ \n██║    ██║██╔════╝    ██╔══██╗██╔══██╗██╔════╝    ██╔══██╗██╔══██╗╚══██╔══╝██╔══██╗██║     ██╔══██╗██╔══██╗\n██║ █╗ ██║█████╗      ███████║██████╔╝█████╗      ██║  ██║███████║   ██║   ███████║██║     ███████║██████╔╝\n██║███╗██║██╔══╝      ██╔══██║██╔══██╗██╔══╝      ██║  ██║██╔══██║   ██║   ██╔══██║██║     ██╔══██║██╔══██╗\n╚███╔███╔╝███████╗    ██║  ██║██║  ██║███████╗    ██████╔╝██║  ██║   ██║   ██║  ██║███████╗██║  ██║██████╔╝\n ╚══╝╚══╝ ╚══════╝    ╚═╝  ╚═╝╚═╝  ╚═╝╚══════╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝╚═════╝ `;
    console.log("%c" + ascii, "color: #000; font-family: monospace; font-size: 10px; line-height: 12px;");
    console.log("%cDesign by wearedatalab.com - contact us", "color: #97EA3F; font-size: 14px; font-weight: bold;");
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              className: 'bg-card border-border text-foreground',
            }}
          />
          <ImpersonationWrapper />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/reservar" element={<ReservationWizard />} />
            <Route path="/acceder" element={<AuthPage />} />
            <Route path="/payment/success" element={<PaymentResult status="success" />} />
            <Route path="/payment/fail" element={<PaymentResult status="fail" />} />
            <Route path="/mis-reservas" element={
              <ProtectedRoute><MyReservations /></ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute roles={["admin"]}><AdminPanel /></ProtectedRoute>
            } />
            <Route path="/sede" element={
              <ProtectedRoute roles={["sede"]}><SedePanel /></ProtectedRoute>
            } />
          </Routes>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
